import React, { useEffect, useState } from 'react';
import { X, Minus, Plus, Calendar as CalIcon } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const todayStr = () => new Date().toISOString().slice(0, 10);
const tomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const Counter = ({ label, value, setValue, min = 0, max = 8 }) => (
  <div className="flex items-center justify-between py-4 border-b border-neutral-200">
    <span className="tracking-[0.2em] text-[11px] uppercase text-neutral-600">{label}</span>
    <div className="flex items-center gap-4">
      <button
        onClick={() => setValue(Math.max(min, value - 1))}
        className="w-9 h-9 border border-neutral-300 text-neutral-700 hover:bg-neutral-900 hover:text-white transition-colors flex items-center justify-center"
        aria-label={`Decrease ${label}`}
      ><Minus className="w-4 h-4" strokeWidth={1.25} /></button>
      <span className="w-6 text-center font-serif text-xl">{value}</span>
      <button
        onClick={() => setValue(Math.min(max, value + 1))}
        className="w-9 h-9 border border-neutral-300 text-neutral-700 hover:bg-neutral-900 hover:text-white transition-colors flex items-center justify-center"
        aria-label={`Increase ${label}`}
      ><Plus className="w-4 h-4" strokeWidth={1.25} /></button>
    </div>
  </div>
);

const BookingModal = ({ open, onClose }) => {
  const [checkIn, setCheckIn] = useState(todayStr());
  const [checkOut, setCheckOut] = useState(tomorrowStr());
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [rooms, setRooms] = useState(1);
  const { toast } = useToast();

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    toast({
      title: 'Availability requested',
      description: `${checkIn} → ${checkOut} · ${adults} adults · ${children} children · ${rooms} room(s). Our reservations team will confirm shortly.`,
    });
    onClose();
  };

  return (
    <div
      className={`fixed inset-0 z-[60] flex items-end md:items-center justify-center transition-opacity duration-300 ${
        open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      <button
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
        aria-label="Close booking"
      />
      <div
        className={`relative w-full md:max-w-lg bg-white text-neutral-900 shadow-2xl transition-transform duration-500 ${
          open ? 'translate-y-0' : 'translate-y-full md:translate-y-4'
        }`}
      >
        <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-neutral-200">
          <div>
            <div className="tracking-[0.32em] text-[10px] uppercase text-neutral-500">Reservations</div>
            <div className="font-serif text-xl mt-1">Book Your Stay</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-neutral-500 hover:text-neutral-900">
            <X className="w-5 h-5" strokeWidth={1.25} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 md:px-8 py-6">
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="tracking-[0.2em] text-[10px] uppercase text-neutral-500">Check-in</span>
              <div className="relative mt-2">
                <input
                  type="date"
                  value={checkIn}
                  min={todayStr()}
                  onChange={(e) => {
                    setCheckIn(e.target.value);
                    if (e.target.value >= checkOut) {
                      const d = new Date(e.target.value); d.setDate(d.getDate() + 1);
                      setCheckOut(d.toISOString().slice(0, 10));
                    }
                  }}
                  className="w-full border border-neutral-300 h-11 pl-3 pr-9 focus:outline-none focus:border-neutral-900"
                />
                <CalIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" strokeWidth={1.25} />
              </div>
            </label>
            <label className="block">
              <span className="tracking-[0.2em] text-[10px] uppercase text-neutral-500">Check-out</span>
              <div className="relative mt-2">
                <input
                  type="date"
                  value={checkOut}
                  min={checkIn}
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full border border-neutral-300 h-11 pl-3 pr-9 focus:outline-none focus:border-neutral-900"
                />
                <CalIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" strokeWidth={1.25} />
              </div>
            </label>
          </div>

          <div className="mt-4">
            <Counter label="Adults" value={adults} setValue={setAdults} min={1} />
            <Counter label="Children" value={children} setValue={setChildren} />
            <Counter label="Rooms" value={rooms} setValue={setRooms} min={1} max={5} />
          </div>

          <button
            type="submit"
            className="mt-6 w-full h-12 bg-neutral-900 text-white tracking-[0.22em] text-[11px] uppercase hover:bg-neutral-800 transition-colors"
          >
            Search Availability
          </button>
          <p className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
            Demo reservation panel. No real booking is created — this is a design showcase.
          </p>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
